import { queryOptions, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import type { DateRange, DateTime } from '@cast/shared-types';
import {
  WorkloadEfficiencyDetailsResponse,
  WorkloadEfficiencyDetailsResponseRaw,
  WorkloadEfficiencyResourceMetrics,
  WorkloadEfficiencyResourceMetricsRaw,
} from '@cast/types';
import { toBig } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const WORKLOAD_EFFICIENCY_DETAILS_QUERY_KEY = 'workload-efficiency-details';

type UseWorkloadEfficiencyDetailsQuery = {
  isLoading: boolean;
  data?: WorkloadEfficiencyDetailsResponse | null;
  error?: unknown;
};

const toNumeric = (
  metrics: WorkloadEfficiencyResourceMetricsRaw<string>
): WorkloadEfficiencyResourceMetrics => ({
  cpu: toBig(metrics.cpu),
  memoryGib: toBig(metrics.memoryGib),
});

const normalizeResponse = ({
  containers,
  currentPodCount,
  waste,
  costImpact,
  noDataReason,
  metricsServerAvailable,
}: WorkloadEfficiencyDetailsResponseRaw): WorkloadEfficiencyDetailsResponse => {
  return {
    containers: containers?.map(({ name, current, items }) => ({
      name,
      current: current
        ? {
            efficiency: toBig(current.efficiency).mul(100).toNumber(),
            recommendation: toNumeric(current.recommendation),
            requests: toNumeric(current.requests),
            usage: toNumeric(current.usage),
          }
        : undefined,
      items: items.map(({ timestamp, info }) => {
        const total = toBig(info.costImpact.onDemand)
          .add(info.costImpact.spot)
          .add(info.costImpact.spotFallback);

        return {
          timestamp: dayjs.utc(timestamp).subtract(1, 'hour').toISOString(),
          efficiency: toBig(info.efficiency).mul(100).toNumber(),
          recommendation: toNumeric(info.recommendation),
          requests: toNumeric(info.requests),
          usage: toNumeric(info.usage),
          costImpact: {
            onDemand: toBig(info.costImpact.onDemand),
            spot: toBig(info.costImpact.spot),
            spotFallback: toBig(info.costImpact.spotFallback),
            total,
          },
        };
      }),
    })),
    currentPodCount: Number(currentPodCount || 0),
    costImpact: costImpact
      ? {
          onDemand: toBig(costImpact.onDemand || 0),
          spot: toBig(costImpact.spot || 0),
          spotFallback: toBig(costImpact.spotFallback || 0),
          total: toBig(costImpact.onDemand || 0)
            .add(toBig(costImpact.spot || 0))
            .add(toBig(costImpact.spotFallback || 0)),
        }
      : undefined,
    waste: waste
      ? {
          cpu: toBig(waste.cpu || 0),
          memoryGib: toBig(waste.memoryGib || 0),
        }
      : undefined,
    noDataReason,
    metricsServerAvailable,
  };
};

type WorkloadEfficiencyDetailsQueryArgs = {
  clusterId: string;
  namespace: string;
  workloadType: string;
  workloadName: string;
  dateRange: DateRange;
  useListingPrices: boolean;
  stepSeconds?: number;
  includeCurrent?: boolean;
  includeHistory?: boolean;
};

type WorkloadEfficiencyDetailsQueryKeyParams = {
  clusterId: string;
  namespace: string;
  workloadType: string;
  workloadName: string;
  from: DateTime;
  to: DateTime;
  useListingPrices: boolean;
  stepSeconds: number;
  includeCurrent: boolean;
  includeHistory: boolean;
};

const getWorkloadEfficiencyDetailsQueryKey = ({
  clusterId,
  namespace,
  workloadType,
  workloadName,
  from,
  to,
  useListingPrices,
  stepSeconds,
  includeCurrent,
  includeHistory,
}: WorkloadEfficiencyDetailsQueryKeyParams) =>
  [
    WORKLOAD_EFFICIENCY_DETAILS_QUERY_KEY,
    clusterId,
    namespace,
    workloadType,
    workloadName,
    from.toISOString(),
    to.toISOString(),
    useListingPrices,
    stepSeconds,
    includeCurrent,
    includeHistory,
  ] as const;

const getWorkloadEfficiencyDetailsQueryOptions = ({
  clusterId,
  namespace,
  workloadType,
  workloadName,
  dateRange: [from, to],
  useListingPrices,
  stepSeconds = 3600,
  includeCurrent = true,
  includeHistory = true,
}: WorkloadEfficiencyDetailsQueryArgs) =>
  queryOptions({
    queryKey: getWorkloadEfficiencyDetailsQueryKey({
      clusterId,
      namespace,
      workloadType,
      workloadName,
      from,
      to,
      useListingPrices,
      stepSeconds,
      includeCurrent,
      includeHistory,
    }),
    enabled: !!clusterId && !!namespace && !!workloadType && !!workloadName,
    queryFn: async () => {
      const { data } = await apiClient.costReport.getWorkloadEfficiencyDetails({
        clusterId: clusterId,
        namespace,
        workloadName,
        workloadType,
        startTime: from.toISOString(),
        endTime: to.toISOString(),
        stepSeconds,
        includeCurrent,
        includeHistory,
        useListingPrices,
      });
      if (data) {
        return normalizeResponse(data);
      }
      return null;
    },
  });

export const useWorkloadEfficiencyDetailsQuery = (
  props: WorkloadEfficiencyDetailsQueryArgs
): UseWorkloadEfficiencyDetailsQuery => {
  const { isLoading, data, error } = useQuery(
    getWorkloadEfficiencyDetailsQueryOptions(props)
  );

  return {
    data,
    isLoading,
    error,
  };
};
